import React from 'react';

import {
  type CaraAttachment,
  type CaraFileAttachment,
  formatFileSize
} from '~/components/cara-input/cara-attachment';

/**
 * A single human-readable validation problem about an attachment. Each
 * error carries the offending attachment's id so the UI can highlight the
 * matching chip if it wants to.
 */
export type CaraAttachmentError = {
  attachmentId: string;
  code: 'empty-file' | 'file-too-large';
  message: string;
};

/**
 * Inspects the current attachment queue for client-side problems that
 * should block submit and surface a message to the user:
 *
 *  - `empty-file`     — a `File` with `size === 0`. Almost always means the
 *                       user dragged a placeholder/zero-byte file by mistake.
 *  - `file-too-large` — only checked when `maxFileSizeBytes` is provided.
 *
 * The hook is pure: it derives errors from the attachments via `useMemo`
 * and never mutates state. Callers wire `isInvalid` into the submit
 * button's `disabled` and render `errors` below the action row.
 */
export function useAttachmentValidation(args: {
  attachments: CaraAttachment[];
  /** Optional per-file ceiling; when omitted, only the empty-file check runs. */
  maxFileSizeBytes?: number;
}): {
  errors: CaraAttachmentError[];
  isInvalid: boolean;
} {
  const { attachments, maxFileSizeBytes } = args;

  const errors = React.useMemo<CaraAttachmentError[]>(() => {
    const out: CaraAttachmentError[] = [];
    for (const a of attachments) {
      if (a.kind !== 'file') continue;
      const fa = a as CaraFileAttachment;
      if (fa.file.size === 0) {
        out.push({
          attachmentId: fa.id,
          code: 'empty-file',
          message: `\u201C${fa.file.name}\u201D is empty. Please upload a readable file.`
        });
        continue;
      }
      if (maxFileSizeBytes && fa.file.size > maxFileSizeBytes) {
        out.push({
          attachmentId: fa.id,
          code: 'file-too-large',
          message: `\u201C${fa.file.name}\u201D is ${formatFileSize(
            fa.file.size
          )} (max ${formatFileSize(maxFileSizeBytes)}).`
        });
      }
    }
    return out;
  }, [attachments, maxFileSizeBytes]);

  return {
    errors,
    isInvalid: errors.length > 0
  };
}
